import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, MapPin, Phone, Mail, Sparkles, Shield, Award, Loader2,
  Languages, Map as MapIcon, List as ListIcon, ArrowRight, ChevronDown,
  LayoutGrid, AlertTriangle, Camera as CameraIcon, FileText, Car as CarIcon,
  Scale, Banknote, Clock, CheckCircle2, HelpCircle, PhoneCall,
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPECIALIZATIONS = [
  "Haftpflicht", "Kasko", "Wertgutachten", "Hagelschaden", "Oldtimer",
  "Unfallrekonstruktion", "Wiederbeschaffungswert", "Reparaturkalkulation",
  "Lkw / Nutzfahrzeuge", "Motorrad", "Wohnmobil", "E-Fahrzeug",
];

const LANGUAGES = ["Deutsch", "Englisch", "Türkisch", "Russisch", "Polnisch", "Arabisch", "Italienisch"];

export default function PartnerSearch() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [spec, setSpec] = useState(params.get("specialization") || "");
  const [lang, setLang] = useState(params.get("language") || "");
  const [view, setView] = useState("list"); // list | grid | map
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (q) sp.set("q", q);
      if (spec) sp.set("specialization", spec);
      if (lang) sp.set("language", lang);
      const { data } = await axios.get(`${API}/public/partners?${sp.toString()}`);
      setPartners(data.partners || []);
    } catch (e) {
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, [q, spec, lang]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    const sp = {};
    if (q) sp.q = q;
    if (spec) sp.specialization = spec;
    if (lang) sp.language = lang;
    setParams(sp);
  };

  return (
    <div className="min-h-screen bg-white text-zinc-950" data-testid="partner-search-page">
      <SeoHead
        title="Kfz-Gutachter in deiner Nähe finden — Easy Gutachter Partner"
        description="Finde geprüfte Kfz-Sachverständige in deiner Stadt. Schadensgutachten, Wertgutachten und Wertminderung — schnell, digital, transparent."
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="partner-search-logo">
            <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="h-12 w-auto select-none" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <Link to="/" className="text-zinc-600 hover:text-zinc-950">Start</Link>
            <Link to="/pricing" className="text-zinc-600 hover:text-zinc-950">Preise</Link>
            <a href="#unfallhilfe" className="text-zinc-600 hover:text-zinc-950">Unfallhilfe</a>
            <a href="#faq" className="text-zinc-600 hover:text-zinc-950">FAQ</a>
          </nav>
          <Link to="/register">
            <Button size="sm" className="brand-gradient-bg text-white">Partner werden</Button>
          </Link>
        </div>
      </header>

      {/* Hero / Search */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 brand-gradient-soft" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-10 text-center">
          <Badge variant="outline" className="bg-white/85 text-emerald-700 border-emerald-200">
            <Award className="h-3 w-3 mr-1" /> Verifizierte Kfz-Sachverständige · DSGVO-konform
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-5">
            Den richtigen <span className="brand-gradient-text">Kfz-Gutachter</span><br className="hidden sm:block" />
            in deiner Nähe finden.
          </h1>
          <p className="text-lg text-zinc-600 leading-relaxed max-w-2xl mx-auto mt-5">
            Unfall, Schaden oder Wertgutachten? Geprüfte Easy-Gutachter-Partner kümmern sich um dein
            Anliegen — schnell, transparent und vollständig digital.
          </p>

          {/* Search bar */}
          <form onSubmit={onSearch} className="mt-9 max-w-3xl mx-auto" data-testid="partner-search-form">
            <div className="bg-white rounded-2xl border border-zinc-200 shadow-md p-2 flex flex-col sm:flex-row items-stretch gap-2">
              <div className="relative flex-1">
                <MapPin className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ort oder PLZ — z. B. Berlin oder 10115"
                  className="pl-12 h-12 text-base border-0 focus-visible:ring-0 shadow-none"
                  data-testid="partner-search-input"
                />
              </div>
              <Button type="submit" size="lg" className="brand-gradient-bg text-white h-12 px-8" data-testid="partner-search-btn">
                <Search className="h-4 w-4 mr-2" /> Suchen
              </Button>
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-4 text-sm">
              <span className="text-zinc-500">Filter:</span>
              <Select value={spec || "all"} onValueChange={(v) => setSpec(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px] bg-white" data-testid="partner-filter-spec">
                  <SelectValue placeholder="Spezialisierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Spezialisierungen</SelectItem>
                  {SPECIALIZATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={lang || "all"} onValueChange={(v) => setLang(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="partner-filter-lang">
                  <SelectValue placeholder="Sprache" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Sprachen</SelectItem>
                  {LANGUAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {(q || spec || lang) && (
                <Button type="button" variant="ghost" size="sm" onClick={() => { setQ(""); setSpec(""); setLang(""); setParams({}); }}>
                  Filter zurücksetzen
                </Button>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="partner-results-count">
            {loading ? "Suche…" : `${partners.length} ${partners.length === 1 ? "Partner" : "Partner"} gefunden`}
          </h2>
          <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-0.5">
            <button
              onClick={() => setView("list")}
              className={cn("px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition",
                view === "list" ? "bg-emerald-50 text-emerald-700" : "text-zinc-600 hover:text-zinc-900")}
              data-testid="partner-view-list"
            >
              <ListIcon className="h-3.5 w-3.5" /> Liste
            </button>
            <button
              onClick={() => setView("grid")}
              className={cn("px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition",
                view === "grid" ? "bg-emerald-50 text-emerald-700" : "text-zinc-600 hover:text-zinc-900")}
              data-testid="partner-view-grid"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Kacheln
            </button>
            <button
              onClick={() => setView("map")}
              className={cn("px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition",
                view === "map" ? "bg-emerald-50 text-emerald-700" : "text-zinc-600 hover:text-zinc-900")}
              data-testid="partner-view-map"
            >
              <MapIcon className="h-3.5 w-3.5" /> Karte
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Partner…
          </div>
        ) : partners.length === 0 ? (
          <EmptyState />
        ) : view === "list" ? (
          <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100 overflow-hidden" data-testid="partner-list">
            {partners.map((p) => <PartnerListRow key={p.id} p={p} />)}
          </div>
        ) : view === "grid" ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="partner-grid">
            {partners.map((p) => <PartnerCard key={p.id} p={p} />)}
          </div>
        ) : (
          <MapView partners={partners} />
        )}
      </section>

      <UnfallhilfeSection />
      <KostenSection />
      <WerteSection />
      <FaqSection />
      <CtaBanner />

      <Footer />
    </div>
  );
}

// =================================================================
//  Info-Sektionen für Unfallgeschädigte
// =================================================================

function UnfallhilfeSection() {
  const STEPS = [
    { n: 1, icon: AlertTriangle, color: "rose", title: "Unfallstelle absichern", text: "Warnblinkanlage an, Warndreieck aufstellen, Warnweste tragen. Verletzte versorgen, Notruf 112 wenn nötig." },
    { n: 2, icon: PhoneCall, color: "amber", title: "Polizei rufen (110)", text: "Bei Personenschäden, hohem Sachschaden, fehlender Einigung oder Fahrerflucht-Verdacht: immer Polizei rufen — die Unfallaufnahme ist später Gold wert." },
    { n: 3, icon: CameraIcon, color: "cyan", title: "Schaden dokumentieren", text: "Fotos von allen Seiten der Fahrzeuge, Kennzeichen, Unfallort, Bremsspuren, Verkehrszeichen. Lieber zu viele Bilder als zu wenige." },
    { n: 4, icon: FileText, color: "blue", title: "Daten austauschen", text: "Name, Anschrift, Versicherung + Versicherungsnummer des Gegners notieren. Aussagen NICHT unterschreiben — auch keine Schuldzuweisung." },
    { n: 5, icon: Award, color: "teal", title: "Unabhängigen Gutachter beauftragen", text: "Bei Schäden über 750 € hast du das Recht auf einen eigenen Sachverständigen — die gegnerische Versicherung muss ihn bezahlen." },
    { n: 6, icon: Scale, color: "indigo", title: "Anwalt einschalten", text: "Bei nicht selbst verschuldetem Unfall ist auch der Anwalt für dich kostenlos — die Versicherung des Gegners trägt die Anwaltskosten." },
  ];
  return (
    <section id="unfallhilfe" className="border-t border-zinc-200 py-16 lg:py-20 bg-zinc-50/40" data-testid="section-unfallhilfe">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 mb-4">
            <AlertTriangle className="h-3 w-3 mr-1" /> Soforthilfe
          </Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Was tun nach einem Unfall?</h2>
          <p className="text-zinc-600 mt-3 max-w-2xl mx-auto">
            6 Schritte, die du als Unfallgeschädigter unbedingt beachten solltest — in der richtigen Reihenfolge. So sicherst du dir deinen Anspruch auf vollständigen Schadensersatz.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="group rounded-2xl border border-zinc-200 bg-white p-6 hover:border-teal-300 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <div className={`shrink-0 h-12 w-12 rounded-xl flex items-center justify-center text-2xl font-display font-bold bg-${s.color}-50 text-${s.color}-700`}>
                    {s.n}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className={`h-4 w-4 text-${s.color}-600`} />
                      <h3 className="font-display font-bold text-zinc-900 tracking-tight">{s.title}</h3>
                    </div>
                    <p className="text-sm text-zinc-600 leading-relaxed">{s.text}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Warnhinweis */}
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong className="font-display">Wichtig:</strong> Niemals einen Gutachter der gegnerischen Versicherung akzeptieren — die handeln im Interesse des Versicherers, nicht in deinem. Beauftrage immer einen <strong>eigenen, unabhängigen Sachverständigen</strong>.
          </div>
        </div>
      </div>
    </section>
  );
}

function KostenSection() {
  const RIGHTS = [
    { icon: Banknote, title: "Reparaturkosten", text: "Vollständige Erstattung der Werkstattkosten zur Wiederherstellung deines Fahrzeugs — nach Werkstatt-/Stundenverrechnungssätzen freier Wahl." },
    { icon: Scale, title: "Wertminderung", text: "Bei reparierbaren Schäden bekommst du den merkantilen Minderwert (Wertverlust) zusätzlich erstattet — kein Anspruch auf Versicherungs-Bagatell-Tabellen." },
    { icon: CarIcon, title: "Mietwagen / Nutzungsausfall", text: "Mietwagenkosten für die Reparaturdauer ODER pauschale Nutzungsausfallentschädigung (je nach Fahrzeugklasse 23–175 € pro Tag)." },
    { icon: Award, title: "Gutachterkosten", text: "Das Honorar des Sachverständigen trägt vollständig die gegnerische Haftpflichtversicherung — auf BVSK-Basis." },
    { icon: Scale, title: "Anwaltskosten", text: "Auch dein Rechtsanwalt wird von der Gegnerversicherung bezahlt — das ist gesetzlicher Schadensersatzanspruch." },
    { icon: Clock, title: "Auslagenpauschale", text: "25–30 € pauschal für Telefon, Porto und sonstige Mehraufwendungen — automatisch fällig." },
  ];
  return (
    <section id="kosten" className="border-t border-zinc-200 py-16 lg:py-20 bg-white" data-testid="section-kosten">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-4">
            <Banknote className="h-3 w-3 mr-1" /> Deine Rechte
          </Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Was steht dir zu?</h2>
          <p className="text-zinc-600 mt-3 max-w-2xl mx-auto">
            Bei einem unverschuldeten Unfall trägt die Haftpflichtversicherung des Verursachers <strong>alle</strong> entstandenen Kosten — du gehst nicht in Vorleistung.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {RIGHTS.map((r, i) => {
            const Icon = r.icon;
            return (
              <div key={i} className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-emerald-300 hover:shadow-md transition-all">
                <Icon className="h-6 w-6 text-emerald-600 mb-3" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-zinc-900 tracking-tight mb-1.5">{r.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{r.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WerteSection() {
  const VALUES = [
    { label: "Wiederbeschaffungswert (WBW)", text: "Was du heute bezahlen müsstest, um ein gleichwertiges Ersatzfahrzeug bei einem seriösen Händler zu kaufen — inkl. MwSt." },
    { label: "Restwert", text: "Der Wert deines Fahrzeugs nach dem Unfall, also was es im beschädigten Zustand noch wert ist (Verwertungsmarkt)." },
    { label: "Reparaturkosten", text: "Vollständige Kosten der fachgerechten Instandsetzung, kalkuliert nach Hersteller-Vorgaben mit Originalteilen." },
    { label: "Merkantile Wertminderung", text: "Der Wertverlust, den dein Auto trotz fachgerechter Reparatur erleidet — weil Käufer Unfallwagen weniger zahlen." },
    { label: "Reparaturdauer", text: "Voraussichtliche Zeit in Arbeitstagen für die Werkstatt-Instandsetzung — Basis für Mietwagen-/Nutzungsausfall-Anspruch." },
    { label: "Wiederbeschaffungsdauer", text: "Zeit in Kalendertagen, um ein vergleichbares Ersatzfahrzeug am Markt zu finden — relevant bei wirtschaftlichem Totalschaden." },
  ];
  return (
    <section id="werte" className="border-t border-zinc-200 py-16 lg:py-20 bg-zinc-50/40" data-testid="section-werte">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 mb-4">
            <Scale className="h-3 w-3 mr-1" /> Im Gutachten
          </Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Welche Werte ermittelt der Gutachter?</h2>
          <p className="text-zinc-600 mt-3 max-w-2xl mx-auto">
            Diese 6 Kennzahlen findest du in jedem ordnungsgemäßen Schadengutachten — sie sind die Basis für deine Entschädigung.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-3 max-w-4xl mx-auto">
          {VALUES.map((v, i) => (
            <div key={i} className="rounded-xl bg-white border border-zinc-200 p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-zinc-900 text-sm">{v.label}</div>
                <p className="text-[13px] text-zinc-600 leading-relaxed mt-1">{v.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const FAQ = [
    {
      q: "Wer bezahlt das Gutachten?",
      a: "Bei einem unverschuldeten Unfall trägt die Haftpflichtversicherung des Unfallverursachers das Sachverständigenhonorar vollständig — du gehst nicht in Vorleistung. Der Gutachter rechnet direkt mit der Versicherung ab.",
    },
    {
      q: "Ab welcher Schadenshöhe ist ein eigener Gutachter sinnvoll?",
      a: "Bei Schäden ab ca. 750 € (Bagatellgrenze des BGH) hast du Anspruch auf einen eigenen, unabhängigen Sachverständigen. Bei kleineren Bagatell-Schäden reicht meist ein Kostenvoranschlag der Werkstatt.",
    },
    {
      q: "Muss ich den von der Versicherung vorgeschlagenen Gutachter nehmen?",
      a: "NEIN. Du hast freie Wahl. Ein von der gegnerischen Versicherung beauftragter Gutachter handelt im Interesse des Versicherers — beauftrage immer einen unabhängigen Sachverständigen deines Vertrauens.",
    },
    {
      q: "Wie lange dauert die Schadenregulierung?",
      a: "Nach Eingang des Gutachtens bei der Versicherung erfolgt die Regulierung typischerweise innerhalb von 4–6 Wochen. Bei klarer Sachlage und vollständigen Unterlagen oft schneller (2–3 Wochen).",
    },
    {
      q: "Was ist der Unterschied zwischen Reparatur und Totalschaden?",
      a: "Liegen die Reparaturkosten unter 100 % des Wiederbeschaffungswerts (WBW) → Reparatur sinnvoll. Über 130 % WBW → wirtschaftlicher Totalschaden (Auszahlung WBW minus Restwert). Zwischen 100–130 % unter Voraussetzungen reparaturwürdig (Integritätsinteresse, BGH).",
    },
    {
      q: "Kann die Versicherung den Schaden einfach 'aktenmäßig' regulieren?",
      a: "Sie kann es versuchen, aber du musst es nicht akzeptieren. Bestehe auf eine ordnungsgemäße Begutachtung durch einen unabhängigen Sachverständigen — das ist dein gutes Recht.",
    },
    {
      q: "Was kostet ein Kfz-Gutachten?",
      a: "Das Honorar orientiert sich an der BVSK-Honorartabelle und richtet sich nach der Schadenhöhe. Bei einem Schaden von 3.000 € netto liegt das Honorar typisch bei ca. 450–550 € netto. Bei einem unverschuldeten Unfall zahlt das die gegnerische Versicherung.",
    },
    {
      q: "Was ist die merkantile Wertminderung?",
      a: "Selbst nach fachgerechter Reparatur ist dein Auto auf dem Gebrauchtwagenmarkt weniger wert, weil Käufer Unfallwagen meiden. Diesen Wertverlust ermittelt der Gutachter und du bekommst ihn von der Versicherung erstattet — meist 5–15 % der Reparaturkosten.",
    },
  ];
  const [open, setOpen] = React.useState(0);
  return (
    <section id="faq" className="border-t border-zinc-200 py-16 lg:py-20 bg-white" data-testid="section-faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 mb-4">
            <HelpCircle className="h-3 w-3 mr-1" /> FAQ
          </Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Häufig gestellte Fragen</h2>
          <p className="text-zinc-600 mt-3">Die wichtigsten Antworten für Unfallgeschädigte — auf einen Blick.</p>
        </div>

        <div className="space-y-2">
          {FAQ.map((f, i) => (
            <div key={i} className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-display font-bold text-zinc-900 tracking-tight pr-4">{f.q}</span>
                <ChevronDown className={cn("h-4 w-4 text-zinc-400 shrink-0 transition-transform", open === i && "rotate-180")} />
              </button>
              {open === i && (
                <div className="px-5 pb-5 text-sm text-zinc-600 leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50" data-testid="section-cta">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <Sparkles className="h-10 w-10 text-teal-600 mx-auto mb-4" strokeWidth={1.5} />
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
          Brauchst du jetzt einen Gutachter?
        </h2>
        <p className="text-zinc-600 mt-3 max-w-2xl mx-auto">
          Geprüfte Easy-Gutachter-Partner in deiner Nähe — kostenlos für dich als Geschädigter,
          weil die gegnerische Versicherung die Kosten trägt.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 items-center justify-center">
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
            <Button size="lg" className="brand-gradient-bg text-white px-8" data-testid="cta-find-partner-btn">
              <Search className="h-4 w-4 mr-2" /> Partner finden
            </Button>
          </a>
          <a href="tel:+4930555555">
            <Button size="lg" variant="outline" data-testid="cta-call-btn">
              <Phone className="h-4 w-4 mr-2" /> Service-Hotline
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

function PartnerListRow({ p }) {
  return (
    <Link
      to={`/partner/${p.slug}`}
      className="group flex items-center gap-4 px-4 py-4 hover:bg-zinc-50/70 transition-colors"
      data-testid={`partner-list-row-${p.slug}`}
    >
      {/* Avatar */}
      <div className="h-14 w-14 rounded-full overflow-hidden bg-white border border-zinc-100 flex items-center justify-center shrink-0">
        {p.avatar_url ? (
          <img src={`${BACKEND_URL}${p.avatar_url}`} alt={p.name} className="w-full h-full object-cover" />
        ) : (
          <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="w-full h-full object-contain p-1.5" />
        )}
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-semibold text-zinc-900 tracking-tight">{p.name}</span>
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold">
            <Sparkles className="h-2.5 w-2.5 mr-0.5" /> Verifiziert
          </Badge>
        </div>
        <div className="text-xs text-emerald-700 font-semibold mt-0.5">Easy Gutachter · {p.city || "Deutschland"}</div>
        <div className="text-[12px] text-zinc-500 line-clamp-1 mt-1">
          {p.bio_short || "Verifizierter Kfz-Sachverständiger."}
        </div>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {(p.specializations || []).slice(0, 4).map((s) => (
            <span key={s} className="text-[10px] font-semibold bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded">{s}</span>
          ))}
          {(p.specializations || []).length > 4 && (
            <span className="text-[10px] text-zinc-400 self-center">+{p.specializations.length - 4}</span>
          )}
        </div>
      </div>
      {/* Right column: location + arrow */}
      <div className="hidden sm:flex flex-col items-end gap-1 shrink-0 text-right">
        <div className="text-xs text-zinc-500 inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" /> {p.postal_code} {p.city}
        </div>
        <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
          Profil ansehen <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}

function PartnerCard({ p }) {
  return (
    <Link
      to={`/partner/${p.slug}`}
      className="group rounded-xl border border-zinc-200 bg-white overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all flex flex-col"
      data-testid={`partner-card-${p.slug}`}
    >
      <div className="relative aspect-square bg-white flex items-center justify-center overflow-hidden">
        {p.avatar_url ? (
          <img src={`${BACKEND_URL}${p.avatar_url}`} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="w-3/4 max-h-[55%] object-contain" />
        )}
        <div className="absolute top-2 right-2">
          <Badge className="bg-white/95 text-emerald-700 border border-emerald-200 text-[9px] px-1.5 py-0 h-4">
            <Sparkles className="h-2.5 w-2.5 mr-0.5" /> Verifiziert
          </Badge>
        </div>
      </div>
      <div className="p-3.5 flex-1 flex flex-col">
        <div className="font-display text-sm font-bold tracking-tight truncate">{p.name}</div>
        <div className="text-[11px] text-emerald-700 font-semibold truncate">Easy Gutachter · {p.city || "DE"}</div>
        <div className="flex flex-wrap gap-1 mt-2">
          {(p.specializations || []).slice(0, 2).map((s) => (
            <span key={s} className="text-[9px] font-semibold bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded">{s}</span>
          ))}
          {(p.specializations || []).length > 2 && (
            <span className="text-[9px] text-zinc-400 self-center">+{p.specializations.length - 2}</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100 text-[10px] text-zinc-500">
          <span className="inline-flex items-center gap-1 truncate"><MapPin className="h-2.5 w-2.5" /> {p.postal_code}</span>
          <span className="inline-flex items-center gap-0.5 text-emerald-700 font-semibold group-hover:translate-x-0.5 transition-transform">
            <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function MapView({ partners }) {
  // Lightweight OSM/Leaflet-free map: render Google Maps embed centered on first partner's coordinates,
  // with markers list below. Works without API key.
  const withGeo = partners.filter((p) => p.geo_lat && p.geo_lng);
  const center = withGeo[0] || { geo_lat: 51.165, geo_lng: 10.4515 }; // Germany centroid fallback
  const zoom = withGeo.length > 1 ? 6 : 11;
  const src = `https://www.google.com/maps?q=${center.geo_lat},${center.geo_lng}&z=${zoom}&output=embed`;
  return (
    <div className="grid lg:grid-cols-[1fr,360px] gap-6" data-testid="partner-map-view">
      <div className="rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-50 h-[600px]">
        <iframe
          title="Partner-Karte"
          src={src}
          className="w-full h-full"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
        {partners.map((p) => (
          <Link
            key={p.id}
            to={`/partner/${p.slug}`}
            className="block rounded-xl border border-zinc-200 bg-white p-4 hover:border-emerald-300 hover:shadow-md transition"
            data-testid={`partner-map-item-${p.slug}`}
          >
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-full overflow-hidden bg-white border border-zinc-100 flex items-center justify-center shrink-0">
                {p.avatar_url ? (
                  <img src={`${BACKEND_URL}${p.avatar_url}`} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="w-full h-full object-contain p-1.5" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold tracking-tight truncate">{p.name}</div>
                <div className="text-xs text-zinc-500">{p.postal_code} {p.city}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  {(p.specializations || []).slice(0, 2).join(" · ")}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-zinc-300 p-16 text-center bg-zinc-50/40" data-testid="partner-empty">
      <Search className="h-10 w-10 text-zinc-400 mx-auto mb-4" />
      <h3 className="font-display text-xl font-bold">Keine Partner gefunden</h3>
      <p className="text-zinc-500 text-sm mt-2 max-w-md mx-auto">
        In deiner Region ist gerade kein verfügbarer Easy-Gutachter-Partner gelistet. Probier eine
        andere Stadt oder lass den Filter offener.
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-zinc-200 py-8 mt-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4" /> Easy Gutachter · TÜV-zertifiziertes Sachverständigen-Netzwerk
        </div>
        <div className="flex items-center gap-5">
          <Link to="/impressum" className="hover:text-zinc-900">Impressum</Link>
          <Link to="/datenschutz" className="hover:text-zinc-900">Datenschutz</Link>
          <Link to="/" className="hover:text-zinc-900">Start</Link>
        </div>
      </div>
    </footer>
  );
}

function SeoHead({ title, description }) {
  // Lightweight SEO head injector (no react-helmet dependency).
  React.useEffect(() => {
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, [title, description]);
  return null;
}
